import { useMemo } from "react"
import { RackConfiguration, RackDimensions, IPE_DIMENSIONS } from "@/lib/types"

interface CantileverRackProps {
  configuration: RackConfiguration
}

export function CantileverRack({ configuration }: CantileverRackProps) {
  const dimensions = useMemo((): RackDimensions => {
    const columnDims = IPE_DIMENSIONS[configuration.columnType]
    const armDims = IPE_DIMENSIONS[configuration.armType]
    return {
      columnWidth: columnDims.width / 1000,
      columnDepth: columnDims.height / 1000,
      armWidth: armDims.width / 1000,
      armHeight: armDims.height / 1000,
      spacing: configuration.bayWidth / 1000,
    }
  }, [configuration.columnType, configuration.armType, configuration.bayWidth])

  const columnHeightM = configuration.columnHeight / 1000
  const clearanceM = configuration.clearanceTopLevel / 1000
  const armLengthM = configuration.armLength / 1000

  const columnColor = configuration.columnCoating === "RAL 5019 Blue" ? "#2563EB" : "#9CA3AF"
  const armColor = configuration.armCoating === "RAL2008 Orange" ? "#EA580C" : "#9CA3AF"
  const rhsColor = "#6B7280"

  const upperBeamLevel = columnHeightM - clearanceM
  const lowerBeamLevel = dimensions.columnDepth
  const availableHeight = upperBeamLevel - lowerBeamLevel
  const levelSpacing =
    configuration.numberOfArmLevels > 1 ? availableHeight / configuration.numberOfArmLevels : 0

  // Intermediate horizontal tie levels derived from vertical brace spacing
  const intermediateLevels = useMemo(() => {
    const bottomTieY = 0.3
    const topTieY = columnHeightM - 0.3
    const braceHeight = topTieY - bottomTieY
    const maxCrossHeight = 2.5
    const numberOfCrosses = Math.ceil(braceHeight / maxCrossHeight)
    const actualCrossHeight = braceHeight / numberOfCrosses
    const levels: number[] = []
    for (let i = 1; i < numberOfCrosses; i++) {
      levels.push(bottomTieY + i * actualCrossHeight)
    }
    return levels
  }, [columnHeightM])

  const flangeThick = 0.01
  const webThick = 0.006
  const armFlangeThick = 0.008
  const armWebThick = 0.005
  const rhsW = 0.08
  const rhsH = 0.05
  const tubeW = 0.03
  const tubeH = 0.01

  function IPEColumn({ position }: { position: [number, number, number] }) {
    return (
      <group position={position}>
        <mesh castShadow receiveShadow position={[dimensions.columnDepth / 2 - flangeThick / 2, 0, 0]}>
          <boxGeometry args={[flangeThick, columnHeightM, dimensions.columnWidth]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh castShadow receiveShadow position={[-dimensions.columnDepth / 2 + flangeThick / 2, 0, 0]}>
          <boxGeometry args={[flangeThick, columnHeightM, dimensions.columnWidth]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh castShadow receiveShadow>
          <boxGeometry args={[dimensions.columnDepth - flangeThick * 2, columnHeightM, webThick]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
      </group>
    )
  }

  function ArmPlate({ position, dir }: { position: [number, number, number]; dir: number }) {
    const plateH = dimensions.armHeight * 1.4
    const offsetX = dir * (dimensions.columnDepth / 2 + flangeThick / 2)
    return (
      <mesh castShadow receiveShadow position={[position[0] + offsetX, position[1], position[2]]}>
        <boxGeometry args={[flangeThick, plateH, dimensions.armWidth]} />
        <meshStandardMaterial color={armColor} metalness={0.7} roughness={0.3} />
      </mesh>
    )
  }

  function IPEArm({ position, dir }: { position: [number, number, number]; dir: number }) {
    return (
      <group position={position}>
        <mesh castShadow receiveShadow position={[dir * armLengthM / 2, dimensions.armHeight / 2 - armFlangeThick / 2, 0]}>
          <boxGeometry args={[armLengthM, armFlangeThick, dimensions.armWidth]} />
          <meshStandardMaterial color={armColor} metalness={0.7} roughness={0.3} />
        </mesh>
        <mesh castShadow receiveShadow position={[dir * armLengthM / 2, -dimensions.armHeight / 2 + armFlangeThick / 2, 0]}>
          <boxGeometry args={[armLengthM, armFlangeThick, dimensions.armWidth]} />
          <meshStandardMaterial color={armColor} metalness={0.7} roughness={0.3} />
        </mesh>
        <mesh castShadow receiveShadow position={[dir * armLengthM / 2, 0, 0]}>
          <boxGeometry args={[armLengthM, dimensions.armHeight - armFlangeThick * 2, armWebThick]} />
          <meshStandardMaterial color={armColor} metalness={0.7} roughness={0.3} />
        </mesh>
      </group>
    )
  }

  function BaseProfile({ position }: { position: [number, number, number] }) {
    const singleLen = dimensions.columnDepth / 2 + armLengthM + 0.05
    const doubleLen = armLengthM * 2 + dimensions.columnWidth + 0.1
    const len = configuration.rackType === "single" ? singleLen : doubleLen
    const offsetX = configuration.rackType === "single" ? len / 2 - dimensions.columnDepth / 2 : 0

    return (
      <group position={[position[0] + offsetX, position[1], position[2]]}>
        <mesh receiveShadow position={[0, dimensions.columnDepth / 2 - flangeThick / 2, 0]}>
          <boxGeometry args={[len, flangeThick, dimensions.columnWidth]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh receiveShadow position={[0, -dimensions.columnDepth / 2 + flangeThick / 2, 0]}>
          <boxGeometry args={[len, flangeThick, dimensions.columnWidth]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
        <mesh receiveShadow>
          <boxGeometry args={[len, dimensions.columnDepth - flangeThick * 2, webThick]} />
          <meshStandardMaterial color={columnColor} metalness={0.8} roughness={0.2} />
        </mesh>
      </group>
    )
  }

  function RHSProfile({ position, length }: { position: [number, number, number]; length: number }) {
    return (
      <mesh castShadow receiveShadow position={position}>
        <boxGeometry args={[rhsW, rhsH, length]} />
        <meshStandardMaterial color={rhsColor} metalness={0.6} roughness={0.4} />
      </mesh>
    )
  }

  function TubeProfile({
    position,
    length,
    rotation,
  }: {
    position: [number, number, number]
    length: number
    rotation?: [number, number, number]
  }) {
    return (
      <mesh castShadow receiveShadow position={position} rotation={rotation}>
        <boxGeometry args={[tubeW, tubeH, length]} />
        <meshStandardMaterial color={rhsColor} metalness={0.6} roughness={0.4} />
      </mesh>
    )
  }

  function VerticalBraceCrosses({ connectionIndex }: { connectionIndex: number }) {
    const connectionZ = -(connectionIndex + 0.5) * dimensions.spacing
    const bottomTieY = 0.3
    const topTieY = columnHeightM - 0.3
    const braceHeight = topTieY - bottomTieY
    const maxCrossHeight = 2.5
    const numberOfCrosses = Math.ceil(braceHeight / maxCrossHeight)
    const actualCrossHeight = braceHeight / numberOfCrosses
    const crossSpan = dimensions.spacing
    const diagonalLength = Math.sqrt(crossSpan ** 2 + actualCrossHeight ** 2)

    return (
      <group>
        {Array.from({ length: numberOfCrosses }, (_, ci) => {
          const crossBottomY = bottomTieY + ci * actualCrossHeight
          const crossTopY = crossBottomY + actualCrossHeight
          const crossCenterY = (crossBottomY + crossTopY) / 2
          const angleX = Math.atan2(actualCrossHeight, crossSpan)
          return (
            <group key={ci}>
              <TubeProfile
                position={[0, crossCenterY, connectionZ]}
                length={diagonalLength}
                rotation={[angleX, 0, 0]}
              />
              <TubeProfile
                position={[0, crossCenterY, connectionZ]}
                length={diagonalLength}
                rotation={[-angleX, 0, 0]}
              />
            </group>
          )
        })}
      </group>
    )
  }

  return (
    <group>
      {/* Columns, base profiles, arms */}
      {Array.from({ length: configuration.numberOfBays }, (_, bi) => {
        const bayZ = -bi * dimensions.spacing
        const startX = dimensions.columnDepth / 2

        return (
          <group key={bi}>
            <BaseProfile position={[0, dimensions.columnDepth / 2, bayZ]} />
            <IPEColumn position={[0, columnHeightM / 2, bayZ]} />

            {Array.from({ length: configuration.numberOfArmLevels }, (_, li) => {
              const armY = lowerBeamLevel + levelSpacing + li * levelSpacing
              return (
                <group key={li}>
                  <ArmPlate position={[0, armY, bayZ]} dir={1} />
                  <IPEArm position={[startX, armY, bayZ]} dir={1} />
                  {configuration.rackType === "double" && (
                    <>
                      <ArmPlate position={[0, armY, bayZ]} dir={-1} />
                      <IPEArm position={[-startX, armY, bayZ]} dir={-1} />
                    </>
                  )}
                </group>
              )
            })}
          </group>
        )
      })}

      {/* Horizontal RHS ties between bays */}
      {Array.from({ length: configuration.numberOfBays - 1 }, (_, ci) => {
        const connectionZ = -(ci + 0.5) * dimensions.spacing
        return (
          <group key={`tie-${ci}`}>
            <RHSProfile position={[0, 0.3, connectionZ]} length={dimensions.spacing} />
            <RHSProfile position={[0, columnHeightM - 0.3, connectionZ]} length={dimensions.spacing} />
          </group>
        )
      })}

      {/* Intermediate horizontal RHS ties */}
      {intermediateLevels.map((levelY, li) =>
        Array.from({ length: configuration.numberOfBays - 1 }, (_, ci) => {
          const connectionZ = -(ci + 0.5) * dimensions.spacing
          return (
            <RHSProfile
              key={`int-${li}-${ci}`}
              position={[0, levelY, connectionZ]}
              length={dimensions.spacing}
            />
          )
        })
      )}

      {/* Vertical brace crosses */}
      {Array.from({ length: configuration.numberOfBays - 1 }, (_, ci) => {
        const shouldBrace = ci === 0 || (ci + 1) % 5 === 0
        if (!shouldBrace) return null
        return <VerticalBraceCrosses key={`brace-${ci}`} connectionIndex={ci} />
      })}
    </group>
  )
}
